import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import { Select } from 'ts/interfaces/Generic';
import Button from 'components/ui/Button/Button';
import {
  BookingAges,
  BookingModel,
  ChangedPrice,
  Pax,
} from 'ts/flight/interfaces/FlightBookResponse';
import { genderOptions } from 'constants/flight';
import { getFlightPassengerValidation } from 'constants/flight';
import { getStaticData, sendRequest, updatePaymentBlock } from 'services/flight';
import { BookOptionsCatalogue } from 'ts/flight/classes/BookOptionsCatalogue';
import BookPayment from 'components/shared/BookPayment';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import { Credit2000PaymentInfo, PrivilegesInfo } from 'ts/shared/interfaces/Generic';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import PhoneInput from 'components/shared/ui/PhoneInput';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { getBookingAges, getBookingAgesStr } from 'utils/flight/flight';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { validate } from 'utils/validation';
import { addYears, showError, showWarning, formatPrice } from 'utils/utils';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import OptionalServices from './OptionalServices';
import SuccessPassengerDetails from './SuccessPassengerDetails';
import { b2b } from 'constants/generic';

interface OrderBookProps {
  bookingModel: BookingModel;
  restrictOptionalService: boolean;
  restrictOptionalServicePair: boolean;
  privileges: PrivilegesInfo;
  isMulti: boolean;
  onSubmit: () => void;
  couponCode: string;
  setAddPrice: (x: number) => void;
  addPrice: number;
  onUpdateCredit: (x: Credit2000PaymentInfo) => void;
  paxes: Pax[];
  setPaxes: (x: Pax[]) => void;
  netravelPage?: CmsPageResponse;
}

const OrderBook: FC<OrderBookProps> = ({
  bookingModel: model,
  restrictOptionalService,
  privileges,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paxes, setPaxes] = useState<Pax[]>(model.paxes);
  const [airlines, setAirlines] = useState<Select[]>([]);
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const catalogue = useRef(
    new BookOptionsCatalogue(t, model.baggageFlightDetails.bookFareAndBaggageFlights)
  );
  const cataloguePair = useRef(
    new BookOptionsCatalogue(t, model.baggageFlightDetailsPair?.bookFareAndBaggageFlights, true)
  );
  const [agesList, setAgesList] = useState<BookingAges>();
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');
  const isPair = !!urlParams.get('pairId');
  const { pathname } = useLocation();
  const [changedPrice, setChangedPrice] = useState<ChangedPrice>();
  const [changedPricePair, setChangedPricePair] = useState<ChangedPrice>();
  const [cannotBookFlight, setCannotBookFlight] = useState(false);
  const newCredit2000 = useRef<Credit2000PaymentInfo>();

  useEffect(() => {
    if (b2b) {
      if (pathname.toLowerCase() !== `/flight/book`) {
        return;
      }
    }
    (async () => {
      const staticData = await getStaticData();
      setAirlines(staticData?.flightAirlines || []);
      setAgesList(staticData?.bookingAges);

      const newPaxes = paxes.map((pax) => ({
        ...pax,
        dob: dateFormat(
          model.flight.outbound
            ? addYears(
              -getBookingAges(pax.paxTypeCode, staticData?.bookingAges).min,
              new Date(model.flight.outbound?.departureDateTime)
            )
            : new Date(),
          'yyyy-MM-dd'
        ),
        gender: 'M',
      }));
      setPaxes(newPaxes);

      props.setPaxes(newPaxes);
    })();
  }, [pathname]); // eslint-disable-line

  const onClickSubmit = async (credit2000: Credit2000PaymentInfo) => {
    setIsSubmitted(true);
    newCredit2000.current = credit2000;

    if (isValid()) {
      setIsLoading(true);
      const body = {
        ...model,
        paxes,
        baggageFlightDetails: {
          bookFareAndBaggageFlights: undefined,
        },
        flight: {
          CommissionType: '',
          ReservationError: {},
          SellFlighPrice: {},
        },
        searchToken: urlParams.get('searchToken'),
        creditCard: true,
        credit2000PaymentInfo: credit2000,
        paymentSettings: credit2000,
        tripId,
        companyId,
        couponAmount: parseInt(props.couponCode || '0') as any,
        confirmPriceChangedBook: changedPrice ? true : undefined,
      };

      let pairParams = {};
      if (isPair) {
        pairParams = {
          flightPair: {
            CommissionType: '',
            ReservationError: {},
            SellFlighPrice: {},
          },
          baggageFlightDetailsPair: {
            bookFareAndBaggageFlights: undefined,
          },
          paxes: paxes.map((pax) => ({
            ...pax,
            baggageOutbound: pax.baggageOutboundArr?.join(','),
            baggageInbound: '',
            baggageInboundArr: [],
            baggageOutboundArr: pax.baggageOutboundArr,
          })),
          paxesPair: paxes.map((pax) => ({
            ...pax,
            baggageOutbound: pax.baggageInboundArr?.join(','),
            baggageInbound: '',
            baggageInboundArr: [],
            baggageOutboundArr: pax.baggageInboundArr,
          })),
        };
      }

      await props.onSubmit();
      const response = await sendRequest({ ...body, ...pairParams });
      if (response) {
        if (response.data?.cannotBookFlight || response.data?.cannotBookFlightPair) {
          setCannotBookFlight(true);
          showWarning('You cannot continue book this flight');
        } else if (
          response.data?.changedPrice?.priceChanged ||
          response.data?.changedPricePair?.priceChanged
        ) {
          setChangedPrice({ ...response.data.changedPrice, isSend: false });
          setChangedPricePair({ ...response.data.changedPricePair, isSend: false });
        } else if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('Flights/BookComplete ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsFlights');
          sessionStorage.setItem('flightPaymentOrder', JSON.stringify(response));
          navigate('/flight/payment');
        }
      }
      setIsLoading(false);
    }
  };

  const isValid = () => {
    let result = true;
    paxes.forEach((pax, index) => {
      const validation = getFlightPassengerValidation(index);
      if (!validate(validation, pax)) {
        result = false;
      }
    });

    return result && termsAccepted;
  };

  const onClickSend = async () => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);

      const body = {
        ...model,
        paxes,
        paymentSettings: model.credit2000PaymentInfo,
        baggageFlightDetails: {
          bookFareAndBaggageFlights: undefined,
        },
        flight: {
          CommissionType: '',
          ReservationError: {},
          SellFlighPrice: {},
        },
        searchToken: urlParams.get('searchToken'),
        tripId,
        companyId,
        confirmPriceChangedBook: changedPrice ? true : undefined,
      };

      let pairParams = {};
      if (isPair) {
        pairParams = {
          flightPair: {
            CommissionType: '',
            ReservationError: {},
            SellFlighPrice: {},
          },
          baggageFlightDetailsPair: {
            bookFareAndBaggageFlights: undefined,
          },
          paxes: paxes.map((pax) => ({
            ...pax,
            baggageOutbound: pax.baggageOutboundArr?.join(','),
            baggageInbound: '',
            baggageInboundArr: [],
            baggageOutboundArr: pax.baggageOutboundArr,
          })),
          paxesPair: paxes.map((pax) => ({
            ...pax,
            baggageOutbound: pax.baggageInboundArr?.join(','),
            baggageInbound: '',
            baggageInboundArr: [],
            baggageOutboundArr: pax.baggageInboundArr,
          })),
        };
      }
      await props.onSubmit();

      const response = await sendRequest({ ...body, ...pairParams });

      if (response) {
        if (response.data?.cannotBookFlight || response.data?.cannotBookFlightPair) {
          setCannotBookFlight(true);
          showWarning('You cannot continue book this flight');
        } else if (
          response.data?.changedPrice?.priceChanged ||
          response.data?.changedPricePair?.priceChanged
        ) {
          setChangedPrice({ ...response.data.changedPrice, isSend: false });
          setChangedPricePair({ ...response.data.changedPricePair, isSend: false });
        } else if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('Flights/BookComplete ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsFlights');
          sessionStorage.setItem('flightSuccessOrder', JSON.stringify(response.data));
          navigate('/flight/success');
        }
      }
      setIsLoading(false);
    }
  };

  const onChangeField = async (newPax: Pax, index: number) => {
    const newPaxes = [...paxes];
    newPaxes[index] = newPax;

    newPaxes.forEach((pax) => {
      pax.baggageOutbound = [
        ...(pax.baggageOutboundArr || []),
        ...(pax.mealOutboundArr || []),
      ].join(',');
      pax.baggageInbound = [...(pax.baggageInboundArr || []), ...(pax.mealInboundArr || [])].join(
        ','
      );
    });
    setPaxes(newPaxes);
    props.setPaxes(newPaxes);

    const addPrice = newPaxes.reduce((acc, pax) => {
      const bagArr = [...(pax.baggageOutboundArr || []), ...(pax.mealOutboundArr || [])];
      const bagCatalogue = [
        ...catalogue.current.outboundBaggage.options,
        ...catalogue.current.outboundMeal.options,
        ...cataloguePair.current.outboundBaggage.options,
        ...cataloguePair.current.outboundMeal.options,
      ];

      const paxPrice = bagArr.reduce((acc, bagId) => {
        const catal = bagCatalogue.find((cat) => cat.value === bagId);
        return acc + (catal?.data.price || 0);
      }, 0);

      const bagArrIn = [...(pax.baggageInboundArr || []), ...(pax.mealInboundArr || [])];
      const bagCatalogueIn = [
        ...catalogue.current.inboundBaggage.options,
        ...catalogue.current.inboundMeal.options,
        ...cataloguePair.current.inboundBaggage.options,
        ...cataloguePair.current.inboundMeal.options,
      ];

      const paxPriceIn = bagArrIn.reduce((acc, bagId) => {
        const catal = bagCatalogueIn.find((cat) => cat.value === bagId);
        return acc + (catal?.data.price || 0);
      }, 0);

      return acc + paxPrice + paxPriceIn;
    }, 0);
    if (props.addPrice !== addPrice) {
      const roundPrice = Math.round(addPrice);
      props.setAddPrice(roundPrice);
      const res = await updatePaymentBlock({
        packageCurrency: model.flight.flighPrice.grossAndTaxWithMarkup.currency || '',
        sourcePrice: model.credit2000PaymentInfo.fullPriceInShekel || 0,
        addPrice: roundPrice + parseInt(props.couponCode || '0'),
        searchToken: urlParams.get('searchToken') || '',
        lang: i18n.language,
      });

      if (!!res) {
        props.onUpdateCredit(res.credit2000PaymentInfo);
      }
    }
  };
  const isOptionsVisible = (paxIndex: number) => {
    const notRestricted = !restrictOptionalService || !props.restrictOptionalServicePair;
    const someVisible = catalogue.current.visible || cataloguePair.current.visible;
    const includesPaxType = [
      ...catalogue.current.outboundBaggage.paxTypes,
      ...catalogue.current.outboundMeal.paxTypes,
      ...catalogue.current.inboundBaggage.paxTypes,
      ...catalogue.current.inboundMeal.paxTypes,
    ].includes(paxes[paxIndex].paxTypeCode);
    return notRestricted && someVisible && includesPaxType;
  };
  const isChangedPrice = !!changedPrice?.airlineCode || !!changedPricePair?.airlineCode;

  return (
    <div className="details book">
      {isChangedPrice && (
        <div>
          <div className="mb-10 text-xsm w100p">
            {!!model?.paxes && <SuccessPassengerDetails paxes={paxes as any} />}
          </div>
          <div className="default-shadow details-block mb-30 fl-pair-low">
            <div>Attention: Price Changed!!!</div>

            <div className="fl-pair-low-sm">
              {!!changedPrice?.airlineCode && (
                <Fragment>
                  <div>
                    {changedPrice.airlineCode} {changedPrice.flightNumber}
                  </div>
                  <div className="color-danger">Base Price, Not Include Baggage Fees!</div>
                  <div>
                    Old Price:{' '}
                    <span className="fl-pair-ticket-ico mr-10" style={{ background: '#3276b1' }}>
                      {formatPrice(
                        Math.round(changedPrice.oldPrice),
                        changedPrice.oldPriceCurrency
                      )}
                    </span>
                    New Price:{' '}
                    <span className="fl-pair-ticket-ico" style={{ background: '#a90329' }}>
                      {formatPrice(
                        Math.round(changedPrice.newPrice),
                        changedPrice.newPriceCurrency
                      )}
                    </span>
                  </div>
                </Fragment>
              )}
              {!!changedPricePair?.airlineCode && (
                <Fragment>
                  <div>
                    {changedPricePair.airlineCode} {changedPricePair.flightNumber}
                  </div>
                  <div className="color-danger">Base Price, Not Include Baggage Fees!</div>
                  <div>
                    Old Price:{' '}
                    <span className="fl-pair-ticket-ico mr-10" style={{ background: '#3276b1' }}>
                      {formatPrice(
                        Math.round(changedPricePair.oldPrice),
                        changedPricePair.oldPriceCurrency
                      )}
                    </span>
                    New Price:{' '}
                    <span className="fl-pair-ticket-ico" style={{ background: '#a90329' }}>
                      {formatPrice(
                        Math.round(changedPricePair.newPrice),
                        changedPricePair.newPriceCurrency
                      )}
                    </span>
                  </div>
                </Fragment>
              )}
            </div>

            <div>To confirm the new price click "Confirm Order"</div>
            <div>To Abort and Ignore please click Ignore</div>
            <div className="color-danger">
              If you click Ignore - you will not be able to rebook the same flights and passengers
              for the next 1-2 hours.
            </div>
          </div>
          <div className="flex-between">
            <Button
              className="btn btn-default"
              title="Ignore"
              icon={faArrowCircleLeft}
              iconclassname="mr-5"
              onClick={() => {
                navigate('/flights');
              }}
            />
            <Button
              title="Confirm Order"
              iconclassname="mr-5"
              disabled={isLoading}
              onClick={() =>
                changedPrice?.isSend ? onClickSend() : onClickSubmit(newCredit2000.current!)
              }
            />
          </div>
        </div>
      )}
      {!changedPrice && (
        <Fragment>
          <div className="book-form default-shadow">
            <div className="title-underline title-header-grey text-lg mb-5">
              {t('passenger.details')}
            </div>{' '}
            {!!props.netravelPage?.page?.languages?.[0]?.title?.body && (
              <div className="netravel-book-1">
                <div className="text-contrast text-bold">
                  {props.netravelPage.page.languages[0].title.desc}
                </div>
                <div
                  className="text-st"
                  dangerouslySetInnerHTML={{
                    __html: props.netravelPage.page.languages[0].title.body,
                  }}
                />
              </div>
            )}
            <div className="color-danger mb-10 text-xsm">{t('only_english_input')}</div>
            <div>
              {paxes.map((pax, index) => (
                <div className="book-form-room" key={index}>
                  <div className="title text-md mb-15">
                    <span className="mr-5">
                      {t('passenger.passenger')} {index + 1}
                    </span>

                    <span className="text-sm text-light text-normal">
                      {getBookingAgesStr(pax.paxTypeCode, agesList)
                        ? `(${getBookingAgesStr(pax.paxTypeCode, agesList)})`
                        : ''}
                    </span>
                  </div>
                  <div className="form-wrapper row">
                    <FormGroupSelect
                      onChange={(gender) => onChangeField({ ...pax, gender }, index)}
                      value={pax.gender}
                      label={t('passenger.gender')}
                      className="col-md-3 col-12"
                      options={genderOptions}
                      needToTranslate={true}
                    />
                    <FormGroup
                      name="firstName"
                      onChange={(firstName) => onChangeField({ ...pax, firstName }, index)}
                      value={pax.firstName}
                      label={t('passenger.fn')}
                      className="col-md-3 col-12"
                      validation={getFlightPassengerValidation(index).firstName}
                      isSubmitted={isSubmitted}
                    />
                    <FormGroup
                      name="lastName"
                      onChange={(lastName) => onChangeField({ ...pax, lastName }, index)}
                      value={pax.lastName}
                      label={t('passenger.ln')}
                      className="col-md-3 col-12"
                      validation={getFlightPassengerValidation(index).lastName}
                      isSubmitted={isSubmitted}
                    />
                    <FormGroupDatepicker
                      label={t('passenger.dob')}
                      appendToBody
                      onChange={(dob) =>
                        onChangeField({ ...pax, dob: dateFormat(dob, 'yyyy-MM-dd') }, index)
                      }
                      value={new Date(pax.dob)}
                      className="col-md-3 col-12"
                      validation={getFlightPassengerValidation(index).dob}
                      isSubmitted={isSubmitted}
                      maxDate={
                        model.flight.outbound
                          ? addYears(
                            -getBookingAges(pax.paxTypeCode, agesList).min,
                            new Date(model.flight.outbound.departureDateTime)
                          )
                          : undefined
                      }
                      minDate={
                        model.flight.outbound
                          ? addYears(
                            -getBookingAges(pax.paxTypeCode, agesList).max,
                            new Date(model.flight.outbound.departureDateTime)
                          )
                          : undefined
                      }
                    />
                  </div>

                  {isOptionsVisible(index) && (
                    <div className="book-optional">
                      <div className="title">{t('passenger.optional_services')}</div>
                      <OptionalServices
                        pax={pax}
                        paxIndex={index}
                        onChangeField={onChangeField}
                        flightSource={model.flight.flightSource}
                        catalogue={catalogue.current}
                        restrictOptionalService={restrictOptionalService}
                      />
                      {!!model.flightPair && (
                        <OptionalServices
                          restrictOptionalService={props.restrictOptionalServicePair}
                          pax={pax}
                          paxIndex={index}
                          onChangeField={onChangeField}
                          flightSource={model.flightPair.flightSource}
                          catalogue={cataloguePair.current}
                        />
                      )}
                    </div>
                  )}

                  <div className="form-wrapper row">
                    {index === 0 && (
                      <Fragment>
                        <PhoneInput
                          onChange={(phone) => onChangeField({ ...pax, phone }, index)}
                          value={pax.phone}
                          isSubmitted={isSubmitted}
                          validation={getFlightPassengerValidation(index).phone}
                          className="col-md-6 col-12"
                        />
                        <FormGroup
                          name="email"
                          type="email"
                          onChange={(email) => onChangeField({ ...pax, email }, index)}
                          value={pax.email}
                          label={t('passenger.email')}
                          className="col-md-6 col-12"
                          validation={getFlightPassengerValidation(index).email}
                          isSubmitted={isSubmitted}
                        />
                      </Fragment>
                    )}

                    <FormGroupSelect
                      onChange={(flightFF_AirlineCode) =>
                        onChangeField({ ...pax, flightFF_AirlineCode }, index)
                      }
                      value={pax.flightFF_AirlineCode}
                      label={t('flight.frequent_flyer_airline')}
                      options={airlines}
                      className="col-md-6 col-12"
                    />
                    <FormGroup
                      name="flightFF_Number"
                      onChange={(flightFF_Number) =>
                        onChangeField({ ...pax, flightFF_Number }, index)
                      }
                      value={pax.flightFF_Number}
                      label={t('flight.frequent_flyer_number')}
                      className="col-md-6 col-12"
                    />
                  </div>
                </div>
              ))}
            </div>
            
              <OrderTripCompany
                companyId={companyId}
                tripId={tripId}
                onChangeCompanyId={setCompanyId}
                onChangeTripId={setTripId}
              />
            
            <div className="checkbox-inline">
              <Checkbox
                checked={termsAccepted}
                onChange={setTermsAccepted}
                label={''}
                className="mb-30"
                hasError={isSubmitted && !termsAccepted}
                blicked={true}
                htmlLabel={
                  <Fragment>
                    <span className="mr-10">{t('passenger.confirm')}</span>
                    <span
                      className="btn-lnk"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsConditionsOpen(true);
                      }}
                    >
                      {t('passenger.read_more')}
                    </span>
                  </Fragment>
                }
              />
            </div>
            {isConditionsOpen && (
              <GeneralConditionsModal
                product="Flights"
                onClose={() => setIsConditionsOpen(false)}
              />
            )}
            {!!props.netravelPage?.page?.languages?.[0]?.title_2?.body && (
              <div className="netravel-book-2">
                <div className="text-contrast text-bold">
                  {props.netravelPage.page.languages[0].title_2.desc}
                </div>
                <div
                  className="text-st"
                  dangerouslySetInnerHTML={{
                    __html: props.netravelPage.page.languages[0].title_2.body,
                  }}
                />
              </div>
            )}
            
              <BookPayment
                onClickSend={onClickSend}
                onClickSubmit={onClickSubmit}
                disabled={isLoading || cannotBookFlight}
                privileges={{ ...privileges }}
                credit2000={model.credit2000PaymentInfo}
                isFormValid={isValid()}
              />
            
          </div>
        </Fragment>
      )}
    </div>
  );
};

export default withErrorBoundary(OrderBook, 'OrderBook');
